import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';

const CONTRACTS_LIST_URL = '/contracts/0/100/id/ASC/s=;';

interface UseContractActionsParams {
    apiUrl: string;
    apiKey: string;
    creditOperationId?: string;
    setLoading: (value: boolean) => void;
    hasUnsavedChanges?: boolean;
    hasInvalidDueDates?: boolean;
    setIsCancelModalOpen: (value: boolean) => void;
    setIsFinishModalOpen: (value: boolean) => void;
}

export const useContractActions = ({
    apiUrl,
    apiKey,
    creditOperationId,
    setLoading,
    hasUnsavedChanges = false,
    hasInvalidDueDates = false,
    setIsCancelModalOpen,
    setIsFinishModalOpen,
}: UseContractActionsParams) => {
    const navigate = useNavigate();

    const handleCancelContract = useCallback(async () => {
        try {
            setLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${apiUrl}/contracts/${creditOperationId}/status/CANCELED`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': apiKey || ''
                }
            });
            if (response.ok) {
                toast.success('Contrato cancelado com sucesso!');
                navigate(CONTRACTS_LIST_URL);
            } else {
                toast.error('Erro ao cancelar contrato');
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        } finally {
            setLoading(false);
            setIsCancelModalOpen(false);
        }
    }, [apiUrl, apiKey, creditOperationId, setLoading, setIsCancelModalOpen, navigate]);

    const handleFinishContract = useCallback(async () => {
        if (hasUnsavedChanges) {
            toast.error('Existem alterações não salvas. Salve antes de finalizar o contrato.');
            setIsFinishModalOpen(false);
            return;
        }

        if (hasInvalidDueDates) {
            toast.error('Existem recebíveis sem data de vencimento. Corrija antes de finalizar o contrato.');
            setIsFinishModalOpen(false);
            return;
        }

        try {
            setLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${apiUrl}/contracts/${creditOperationId}/status/SUCCESSFULLY_COMPLETED`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': apiKey || ''
                }
            });
            if (response.ok) {
                toast.success('Contrato finalizado com sucesso!');
                navigate(CONTRACTS_LIST_URL);
            } else {
                toast.error('Erro ao finalizar contrato');
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        } finally {
            setLoading(false);
            setIsFinishModalOpen(false);
        }
    }, [apiUrl, apiKey, creditOperationId, setLoading, hasUnsavedChanges, hasInvalidDueDates, setIsFinishModalOpen, navigate]);

    return { handleCancelContract, handleFinishContract };
};
